import type { Booking, BookingsOverviewState, UpcomingBooking, Voucher } from '../domain/types'
import type { MyPortalNotification } from '@/scopes/common/domain/myPortalNotification'
import { getDaysUntilDeparture as getDaysUntilDepartureFromDate, isOnHolidays } from '@/scopes/account/common/utils/bookingDates'

function sortByDate<T>(
  items: T[],
  getDateField: (item: T) => string,
  order: 'asc' | 'desc' = 'desc',
): T[] {
  return items.sort((a, b) => {
    const dateA = new Date(getDateField(a))
    const dateB = new Date(getDateField(b))

    const timeA = dateA.getTime()
    const timeB = dateB.getTime()

    if (Number.isNaN(timeA) && Number.isNaN(timeB))
      return 0

    if (Number.isNaN(timeA))
      return 1
    if (Number.isNaN(timeB))
      return -1

    return order === 'desc' ? timeB - timeA : timeA - timeB
  })
}

export function isCancelledTabActive(state: BookingsOverviewState) {
  return state.bookingHistoryActiveTab === 'cancelled'
}

export function isPastTabActive(state: BookingsOverviewState) {
  return state.bookingHistoryActiveTab === 'past'
}

export function isPageLoading(state: BookingsOverviewState) {
  return state.bookingOverviewPageLoadStatus === 'loading'
}

export function isPageError(state: BookingsOverviewState) {
  return state.bookingOverviewPageLoadStatus === 'error'
}

export function isPageSuccess(state: BookingsOverviewState) {
  return state.bookingOverviewPageLoadStatus === 'success'
}

function sortBookingsByDate<T extends Booking>(bookings: Record<string, T>, order: 'asc' | 'desc' = 'desc'): T[] {
  return sortByDate(Object.values(bookings), booking => booking.rawDepartureDate, order)
}

export function getSortedPastBookings(state: BookingsOverviewState): Booking[] {
  return sortBookingsByDate(state.pastBookings)
}

export function getSortedCancelledBookings(state: BookingsOverviewState): Booking[] {
  return sortBookingsByDate(state.cancelledBookings)
}

export function getSortedUpcomingBookings(state: BookingsOverviewState): UpcomingBooking[] {
  return sortBookingsByDate(state.upcomingBookings, 'asc')
}

export function getSelectedUpcomingBooking(state: BookingsOverviewState): UpcomingBooking | undefined {
  if (!state.selectedUpcomingBookingId)
    return undefined

  return state.upcomingBookings[state.selectedUpcomingBookingId]
}

export function hasUpcomingBookings(state: BookingsOverviewState): boolean {
  return Object.keys(state.upcomingBookings).length > 0
}

export function isUpcomingBookingOnHolidays(state: BookingsOverviewState, now: Date = new Date()): boolean {
  const selectedUpcomingBooking = getSelectedUpcomingBooking(state)
  const unknownReturnDate = undefined
  return isOnHolidays(selectedUpcomingBooking?.rawDepartureDate, unknownReturnDate, now)
}

export function getDaysUntilDeparture(state: BookingsOverviewState, now: Date = new Date()): number | null {
  const selectedUpcomingBooking = getSelectedUpcomingBooking(state)
  return getDaysUntilDepartureFromDate(selectedUpcomingBooking?.rawDepartureDate, now)
}

export function isAddingBooking(state: BookingsOverviewState) {
  return state.addBookingLoadStatus === 'loading'
}

export function getAddBookingErrorKeyMessage(state: BookingsOverviewState): string {
  const reason = state.addBookingReasonNotLinked

  switch (reason) {
    case 'CustomerAlreadyLinked':
      return 'bookings_overview_add_booking_customer_already_linked'
    case 'DepartureDateDoesNotMatch':
      return 'bookings_overview_add_booking_departure_date_not_matching'
    case 'LastNameDoesNotMatch':
      return 'bookings_overview_add_booking_last_name_not_matching'
    case 'MainBookerNotLinked':
      return 'bookings_overview_add_booking_main_booker_not_linked'
    case 'NotFound':
      return 'bookings_overview_add_booking_not_found'
    case 'NotSupported':
      return 'bookings_overview_add_booking_not_supported'
    default:
      return 'bookings_overview_add_booking_generic_error'
  }
}

export function isAddBookingLoading(state: BookingsOverviewState) {
  return state.addBookingLoadStatus === 'loading'
}

export function isAddBookingError(state: BookingsOverviewState) {
  return state.addBookingLoadStatus === 'error'
}

export function isAddBookingSuccess(state: BookingsOverviewState) {
  return state.addBookingLoadStatus === 'success'
}

const MY_PORTAL_NOTIFICATION_TRANSLATION_KEY: Record<MyPortalNotification, string> = {
  accountVerified: 'bookings_overview_account_verified_notification',
  passwordSet: 'bookings_overview_password_set_notification',
}

export function getSuccessBannerMessageId(state: BookingsOverviewState): string | null {
  if (isAddBookingSuccess(state))
    return 'bookings_overview_add_booking_success'

  if (state.myPortalNotification)
    return MY_PORTAL_NOTIFICATION_TRANSLATION_KEY[state.myPortalNotification]

  return null
}

export function getVouchers(state: BookingsOverviewState): Voucher[] {
  return sortByDate(
    Object.values(state.vouchers),
    voucher => voucher.expirationDate,
    'asc',
  )
}
